import React, { useState, useEffect } from 'react';
import { X, Mail, MailOpen, MessageSquare, AlertCircle, CheckCircle, Paperclip, Send, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { fetchUserPreferences, visibleInApp } from '../utils/notificationPreferences';

interface Message {
  id: string;
  companyId: string;
  configItemId?: string;
  type: 'general' | 'question' | 'blocker' | 'approval' | 'attachment' | 'system';
  subject: string;
  content: string;
  senderId: string;
  senderName: string;
  senderEmail: string;
  senderRole: string;
  readBy: string[];
  createdAt: string;
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    size: number;
  }>;
}

interface Notification {
  id: string;
  userId: string;
  type: 'mention' | 'message' | 'system';
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
  timestamp?: string;
  metadata?: {
    companyId?: string;
    configItemId?: string;
    messageId?: string;
    mentionedBy?: string;
  };
}

type FeedItem = (Message & { itemType: 'message' }) | (Notification & { itemType: 'notification' });

interface MessageCenterProps {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  companyId: string;
  projectId: string;
  publicAnonKey: string;
  onClose: () => void;
  /** Session token. Messaging routes now require a signed-in caller. */
  accessToken?: string;
  initialComposing?: boolean;
  initialSubject?: string;
  initialContent?: string;
}

export function MessageCenter({ userId, userName, userEmail, userRole, companyId, projectId, publicAnonKey, onClose, accessToken, initialComposing = false, initialSubject = '', initialContent = '' }: MessageCenterProps) {
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  // Who a new message reaches (the engagement's consultants and the client team), shown on the composer.
  const [recipients, setRecipients] = useState<Array<{ userId: string; name: string; side: 'client' | 'consultant' }> | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages/recipients?companyId=${encodeURIComponent(companyId)}`, { headers: getAuthHeaders(accessToken) })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setRecipients(Array.isArray(data?.recipients) ? data.recipients : []); })
      .catch(() => { if (!cancelled) setRecipients([]); });
    return () => { cancelled = true; };
  }, [companyId, projectId, accessToken]);
  const consultantNames = (recipients ?? []).filter((r) => r.side === 'consultant').map((r) => r.name);
  const clientCount = (recipients ?? []).filter((r) => r.side === 'client').length;
  const toLine = recipients === null
    ? 'Checking who will receive this…'
    : consultantNames.length > 0
      ? `${consultantNames.join(', ')}${clientCount > 0 ? ` and ${clientCount} of your team` : ''}`
      : clientCount > 0
        ? `${clientCount} of your team (no consultant is assigned yet)`
        : 'Nobody is assigned to this engagement yet; your message will be saved but not delivered.';
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<Message | null>(null);
  const [notificationComment, setNotificationComment] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isComposing, setIsComposing] = useState(initialComposing);
  const [newMessageSubject, setNewMessageSubject] = useState(initialSubject);
  const [newMessageContent, setNewMessageContent] = useState(initialContent);

  // Fetch messages and notifications
  const fetchMessagesAndNotifications = async () => {
    setIsLoading(true);
    try {
      const unreadOnly = filter === 'unread' ? 'true' : 'false';
      
      // Fetch messages, notifications and the in-app preferences in parallel
      const [messagesResponse, notificationsResponse, preferences] = await Promise.all([
        fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages?companyId=${companyId}&userId=${userId}&unreadOnly=${unreadOnly}`,
          {
            headers: getAuthHeaders(accessToken),
          }
        ),
        fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications?userId=${userId}`,
          {
            headers: getAuthHeaders(accessToken),
          }
        ),
        fetchUserPreferences(userId, projectId, '').catch(() => null),
      ]);

      const items: FeedItem[] = [];

      if (messagesResponse.ok) {
        const messagesData = await messagesResponse.json();
        const messages = messagesData.messages || [];
        items.push(...messages.map((m: Message) => ({ ...m, itemType: 'message' as const })));
      }

      if (notificationsResponse.ok) {
        const notificationsData = await notificationsResponse.json();
        // Categories the user turned off in-app stay hidden (the old NotificationCenter did this)
        let notifications = visibleInApp(notificationsData.notifications || [], preferences);
        
        // Filter for unread if needed
        if (filter === 'unread') {
          notifications = notifications.filter((n: Notification) => !n.read);
        }
        
        items.push(...notifications.map((n: Notification) => ({ ...n, itemType: 'notification' as const })));
      }

      // Sort by date (newest first)
      items.sort((a, b) => {
        const aDate = new Date(a.createdAt || (a as any).timestamp).getTime();
        const bDate = new Date(b.createdAt || (b as any).timestamp).getTime();
        return bDate - aDate;
      });

      setFeedItems(items);
    } catch (error) {
      console.error('Error fetching messages and notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMessagesAndNotifications();
  }, [filter]);

  // Mark message as read
  const markAsRead = async (message: Message) => {
    if (message.readBy.includes(userId)) return;

    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages/${message.id}/read`,
        {
          method: 'PATCH',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({ userId, companyId }),
        }
      );

      // Update local state
      setFeedItems(prev =>
        prev.map(item =>
          item.itemType === 'message' && item.id === message.id
            ? { ...item, readBy: [...item.readBy, userId] }
            : item
        )
      );
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark notification as read
  const markNotificationAsRead = async (notification: Notification) => {
    if (notification.read) return;

    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications/${notification.id}/read?userId=${userId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(accessToken),
        }
      );

      // Update local state
      setFeedItems(prev =>
        prev.map(item =>
          item.itemType === 'notification' && item.id === notification.id
            ? { ...item, read: true }
            : item
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Delete a notification (RESTORE_PLAN R2.5: the old NotificationCenter's delete)
  const deleteNotification = async (notification: Notification) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/user/notifications/${encodeURIComponent(notification.id)}?userId=${userId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(accessToken),
        }
      );
      if (response.ok || response.status === 404) {
        setFeedItems(prev => prev.filter(item => !(item.itemType === 'notification' && item.id === notification.id)));
        setSelectedNotification(null);
        setNotificationMessage(null);
        setNotificationComment(null);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Helper function to clean mention markdown from comment text
  const cleanMentionText = (text: string): string => {
    // Support both formats:
    // - Old/markdown format: @[Display Name]({{userId}}) → @Display Name
    // - New format: @[Display Name]{{userId}} → @Display Name
    return text
      .replace(/@\[([^\]]+)\]\(\{\{[^}]+\}\}\)/g, '@$1')  // Old format
      .replace(/@\[([^\]]+)\]\{\{[^}]+\}\}/g, '@$1');     // New format
  };

  // Fetch message or comment associated with notification
  const fetchNotificationContent = async (notification: Notification) => {
    console.log('[MessageCenter] Fetching notification content for:', notification);
    console.log('[MessageCenter] Notification metadata:', JSON.stringify(notification.metadata, null, 2));
    
    const messageId = notification.metadata?.messageId;
    const commentId = notification.metadata?.commentId;
    const itemId = notification.metadata?.itemId;
    
    console.log('[MessageCenter] IDs - messageId:', messageId, 'commentId:', commentId, 'itemId:', itemId);

    // Reset state
    setNotificationMessage(null);
    setNotificationComment(null);
    
    // If it's a comment notification
    if (commentId && itemId && itemId !== 'undefined') {
      setIsLoadingMessage(true);
      try {
        const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${itemId}/comments`;
        console.log('[MessageCenter] Fetching comments from:', url);
        
        const response = await fetch(url, {
          headers: getAuthHeaders(accessToken),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('[MessageCenter] Received comments data:', data);
          // Find the specific comment
          const comment = data.comments?.find((c: any) => c.id === commentId);
          if (comment) {
            console.log('[MessageCenter] Found comment:', comment);
            // Map the comment structure - the server uses 'text' but we display 'content'
            setNotificationComment({
              ...comment,
              content: comment.text,
              timestamp: comment.timestamp
            });
          } else {
            console.log('[MessageCenter] Comment not found in response');
          }
        } else {
          const errorText = await response.text();
          console.error('[MessageCenter] Error response:', errorText);
        }
      } catch (error) {
        console.error('[MessageCenter] Error fetching comment:', error);
      } finally {
        setIsLoadingMessage(false);
      }
      return;
    }
    
    // Handle case where itemId is 'undefined' - don't try to fetch
    if (commentId && itemId === 'undefined') {
      console.log('[MessageCenter] Skipping fetch for notification with undefined itemId');
      return;
    }
    
    // If it's a message notification
    if (messageId) {
      setIsLoadingMessage(true);
      try {
        const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages/${messageId}?companyId=${companyId}`;
        console.log('[MessageCenter] Fetching message from:', url);
        
        const response = await fetch(url, {
          headers: getAuthHeaders(accessToken),
        });

        console.log('[MessageCenter] Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('[MessageCenter] Received message data:', data);
          setNotificationMessage(data.message);
        } else {
          const errorText = await response.text();
          console.error('[MessageCenter] Error response:', errorText);
        }
      } catch (error) {
        console.error('[MessageCenter] Error fetching notification message:', error);
      } finally {
        setIsLoadingMessage(false);
      }
      return;
    }
    
    console.log('[MessageCenter] No messageId or commentId found in notification metadata');
  };

  // Send new message
  const sendMessage = async () => {
    if (!newMessageSubject.trim() || !newMessageContent.trim()) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/messages`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify({
            companyId,
            type: 'general',
            subject: newMessageSubject,
            content: newMessageContent,
            senderId: userId,
            senderName: userName,
            senderEmail: userEmail,
            senderRole: userRole,
          }),
        }
      );

      if (response.ok) {
        setNewMessageSubject('');
        setNewMessageContent('');
        setIsComposing(false);
        fetchMessagesAndNotifications();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'question': return <MessageSquare className="h-4 w-4" />;
      case 'blocker': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'approval': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'attachment': return <Paperclip className="h-4 w-4" />;
      default: return <Mail className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'blocker': return 'bg-red-100 text-red-800';
      case 'approval': return 'bg-green-100 text-green-800';
      case 'question': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = feedItems.filter(item => 
    (item.itemType === 'message' && !item.readBy.includes(userId)) ||
    (item.itemType === 'notification' && !item.read)
  ).length;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between pr-8">
            <div>
              <DialogTitle>Messages</DialogTitle>
              <DialogDescription className="sr-only">
                View and manage your project messages and notifications
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {(selectedMessage || selectedNotification) && !isComposing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedMessage(null);
                    setSelectedNotification(null);
                    setNotificationMessage(null);
                    setNotificationComment(null);
                  }}
                >
                  ← Back to List
                </Button>
              )}
              <Button
                variant={isComposing ? "secondary" : "default"}
                size="sm"
                onClick={() => setIsComposing(!isComposing)}
              >
                <Send className="h-4 w-4 mr-2" />
                New Message
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex overflow-hidden">
          {/* Message List - Only show when nothing selected and not composing */}
          {!selectedMessage && !selectedNotification && !isComposing && (
            <div className="flex-1 flex flex-col">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | 'unread')} className="px-4 pt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="unread">
                  Unread {unreadCount > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {unreadCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <ScrollArea className="flex-1 px-4 pb-4">
              <div className="space-y-2 mt-4">
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : feedItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {filter === 'unread' ? 'No unread items' : 'No messages yet'}
                  </div>
                ) : (
                  feedItems.map((item) => {
                    if (item.itemType === 'message') {
                      const message = item as Message & { itemType: 'message' };
                      const isUnread = !message.readBy.includes(userId);
                      const isSelected = selectedMessage?.id === message.id;

                      return (
                        <button
                          key={`msg-${message.id}`}
                          onClick={() => {
                            setSelectedMessage(message);
                            setSelectedNotification(null);
                            setIsComposing(false);
                            markAsRead(message);
                          }}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            isSelected
                              ? 'bg-primary/10 border-primary'
                              : 'hover:bg-muted border-transparent'
                          } ${isUnread ? 'bg-blue-50' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {isUnread ? (
                                <Mail className="h-4 w-4 text-primary flex-shrink-0" />
                              ) : (
                                <MailOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              )}
                              <span className={`truncate ${isUnread ? 'font-semibold' : 'font-medium'}`}>
                                {message.senderName}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {formatDate(message.createdAt)}
                            </span>
                          </div>
                          <p className={`text-sm mb-1 truncate ${isUnread ? 'font-medium' : ''}`}>
                            {message.subject}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {message.content}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className={`text-xs ${getTypeColor(message.type)}`}>
                              {message.type}
                            </Badge>
                          </div>
                        </button>
                      );
                    } else {
                      const notification = item as Notification & { itemType: 'notification' };
                      const isUnread = !notification.read;
                      const isSelected = selectedNotification?.id === notification.id;

                      return (
                        <button
                          key={`notif-${notification.id}`}
                          onClick={() => {
                            setSelectedNotification(notification);
                            setSelectedMessage(null);
                            setIsComposing(false);
                            markNotificationAsRead(notification);
                            fetchNotificationContent(notification);
                          }}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            isSelected
                              ? 'bg-primary/10 border-primary'
                              : 'hover:bg-muted border-transparent'
                          } ${isUnread ? 'bg-blue-50' : ''}`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            <AlertCircle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isUnread ? 'text-orange-500' : 'text-muted-foreground'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2 mb-1">
                                <span className={`text-sm ${isUnread ? 'font-semibold' : 'font-medium'}`}>
                                  {notification.title}
                                </span>
                                <span className="text-xs text-muted-foreground flex-shrink-0 whitespace-nowrap">
                                  {formatDate(notification.createdAt || notification.timestamp || '')}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {notification.message}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs bg-orange-100 text-orange-800">
                                  {notification.type}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    }
                  })
                )}
              </div>
            </ScrollArea>
            </div>
          )}

          {/* Compose View - Full width */}
          {isComposing && (
            <div className="flex-1 flex flex-col">
              <div className="flex-1 flex flex-col p-6">
                <h3 className="font-semibold mb-2">Compose Message</h3>
                <p className="text-sm text-muted-foreground mb-4"><span className="font-medium text-foreground">To:</span> {toLine}</p>
                <Input
                  placeholder="Subject"
                  value={newMessageSubject}
                  onChange={(e) => setNewMessageSubject(e.target.value)}
                  className="mb-4"
                />
                <Textarea
                  placeholder="Type your message here..."
                  value={newMessageContent}
                  onChange={(e) => setNewMessageContent(e.target.value)}
                  className="flex-1 resize-none mb-4"
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsComposing(false)}>
                    Cancel
                  </Button>
                  <Button onClick={sendMessage} disabled={!newMessageSubject.trim() || !newMessageContent.trim()}>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Message Detail - Full width */}
          {selectedMessage && !isComposing && (
            <div className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{selectedMessage.subject}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-xs ${getTypeColor(selectedMessage.type)}`}>
                            {getMessageIcon(selectedMessage.type)}
                            <span className="ml-1">{selectedMessage.type}</span>
                          </Badge>
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(selectedMessage.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="font-medium">{selectedMessage.senderName}</span>
                      <span>({selectedMessage.senderEmail})</span>
                      <Badge variant="secondary" className="text-xs capitalize">
                        {selectedMessage.senderRole.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                  </div>

                  {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">Attachments</h4>
                      <div className="space-y-2">
                        {selectedMessage.attachments.map((attachment) => (
                          <a
                            key={attachment.id}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 border rounded hover:bg-muted transition-colors"
                          >
                            <Paperclip className="h-4 w-4" />
                            <span className="text-sm">{attachment.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              ({(attachment.size / 1024).toFixed(1)} KB)
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Notification Detail - Full width */}
          {selectedNotification && !isComposing && (
            <div className="flex-1 flex flex-col">
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{selectedNotification.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs bg-orange-100 text-orange-800">
                            {selectedNotification.type}
                          </Badge>
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(selectedNotification.createdAt || selectedNotification.timestamp || '').toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm text-muted-foreground mb-4">{selectedNotification.message}</p>
                  </div>

                  {/* Show the actual message or comment if available */}
                  {isLoadingMessage ? (
                    <div className="text-center py-4 text-muted-foreground">
                      Loading content...
                    </div>
                  ) : selectedNotification.metadata?.itemId === 'undefined' ? (
                    <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                      <p className="text-sm text-yellow-800">
                        This notification is associated with a config item that no longer exists or was not properly recorded.
                        The related content cannot be displayed.
                      </p>
                    </div>
                  ) : notificationComment ? (
                    <div className="border rounded-lg p-4 bg-muted/30">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 mb-2">
                            Comment
                          </Badge>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="font-medium">{notificationComment.userName}</span>
                            <span>({notificationComment.userEmail})</span>
                            <span className="ml-auto">
                              {new Date(notificationComment.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        <p className="whitespace-pre-wrap">{cleanMentionText(notificationComment.content)}</p>
                      </div>
                    </div>
                  ) : notificationMessage ? (
                    <div className="border rounded-lg p-4 bg-muted/30">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={`text-xs ${getTypeColor(notificationMessage.type)}`}>
                              {getMessageIcon(notificationMessage.type)}
                              <span className="ml-1">{notificationMessage.type}</span>
                            </Badge>
                          </div>
                          <h4 className="font-semibold">{notificationMessage.subject}</h4>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <span className="font-medium">{notificationMessage.senderName}</span>
                        <span>({notificationMessage.senderEmail})</span>
                        <Badge variant="secondary" className="text-xs capitalize">
                          {notificationMessage.senderRole.replace('_', ' ')}
                        </Badge>
                        <span className="ml-auto">
                          {new Date(notificationMessage.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="border-t pt-3">
                        <p className="whitespace-pre-wrap">{notificationMessage.content}</p>
                      </div>
                      {notificationMessage.attachments && notificationMessage.attachments.length > 0 && (
                        <div className="border-t pt-3 mt-3">
                          <h5 className="font-medium mb-2 text-sm">Attachments</h5>
                          <div className="space-y-2">
                            {notificationMessage.attachments.map((attachment) => (
                              <a
                                key={attachment.id}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 border rounded hover:bg-muted transition-colors text-sm"
                              >
                                <Paperclip className="h-4 w-4" />
                                <span>{attachment.name}</span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                  ({(attachment.size / 1024).toFixed(1)} KB)
                                </span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className="pt-2 flex items-center gap-2">
                    {selectedNotification.link && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={selectedNotification.link}>
                          View Related Item
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-red-600"
                      onClick={() => void deleteNotification(selectedNotification)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete notification
                    </Button>
                  </div>
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}